import React, { useState, useEffect } from 'react';
import { getSystemHealth } from '../../services/api';

function SystemHealth({ admin }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');

  useEffect(() => {
    fetchHealth();
    // Refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchHealth = async () => {
    try {
      const response = await getSystemHealth();
      setHealth(response.data);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      setError('Failed to load system health: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      case 'unhealthy': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return '✅';
      case 'warning': return '⚠️';
      case 'critical': return '🔴';
      case 'unhealthy': return '🔴';
      default: return '❓';
    }
  };

  const isMetricWithinThreshold = (metricKey, metric) => {
    switch (metricKey) {
      case 'error_rate':
        return parseFloat(metric.current) <= parseFloat(metric.threshold);
      case 'response_time':
        // Assuming response_time is in format like "125ms"
        const currentMs = parseInt(metric.current);
        const thresholdMs = parseInt(metric.threshold);
        return currentMs <= thresholdMs;
      case 'concurrent_users':
        return metric.current <= metric.threshold;
      default:
        return true;
    }
  };

  if (loading && !health) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading system health...</p>
        </div>
      </div>
    );
  }

  if (!health) {
    return (
      <div className="min-h-full bg-gray-50 p-6">
        <div className="alert alert-danger">Failed to load system health</div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Health</h1>
            <p className="text-gray-600 mt-1">Monitor system performance and service status</p>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500">
              Last updated: {lastUpdated}
            </span>
            <button
              onClick={fetchHealth}
              className="bg-[#ff660e] text-white px-4 py-2 rounded-lg hover:bg-[#e55b0c] transition-colors"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <span className="text-red-500">⚠️</span>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Database Health */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Database</h3>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(health.database.status)}`}>
              {getStatusIcon(health.database.status)} {health.database.status.toUpperCase()}
            </span>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Connection</span>
              <span className={`text-sm font-medium ${health.database.connection ? 'text-green-600' : 'text-red-600'}`}>
                {health.database.connection ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Last Check</span>
              <span className="text-sm text-gray-900">
                {new Date(health.database.last_check).toLocaleTimeString()}
              </span>
            </div>

            {health.database.tables && health.database.tables.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Top Tables by Size</h4>
                <div className="space-y-2">
                  {health.database.tables.slice(0, 5).map(table => (
                    <div key={table.table_name} className="flex justify-between text-sm">
                      <span className="text-gray-600">{table.table_name}</span>
                      <span className="text-gray-900">
                        {table.row_count?.toLocaleString()} rows • {table.size_mb} MB
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Storage Health */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Storage</h3>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(health.storage.status)}`}>
              {getStatusIcon(health.storage.status)} {health.storage.status.toUpperCase()}
            </span>
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Disk Usage</span>
                <span className="text-gray-900">{health.storage.used_percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    health.storage.used_percentage > 90 ? 'bg-red-600' :
                    health.storage.used_percentage > 80 ? 'bg-yellow-600' : 'bg-green-600'
                  }`}
                  style={{ width: `${health.storage.used_percentage}%` }}
                ></div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Free Space</div>
                <div className="font-medium text-gray-900">{health.storage.free_space_gb} GB</div>
              </div>
              <div>
                <div className="text-gray-600">Total Space</div>
                <div className="font-medium text-gray-900">{health.storage.total_space_gb} GB</div>
              </div>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Last Check</span>
              <span className="text-sm text-gray-900">
                {new Date(health.storage.last_check).toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>

        {/* Services Health */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Services</h3>
            <span className="text-sm text-gray-500">
              {Object.values(health.services).filter(s => s.status === 'healthy').length} / {Object.keys(health.services).length} Healthy
            </span>
          </div>
          
          <div className="space-y-3">
            {Object.entries(health.services).map(([key, service]) => (
              <div key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className={getStatusColor(service.status).replace('text-', 'text-').replace('bg-', 'bg-')}>
                    {getStatusIcon(service.status)}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{service.name}</div>
                    <div className="text-xs text-gray-500">
                      Last check: {new Date(service.last_check).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(service.status)}`}>
                  {service.status.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Metrics</h3>
          
          <div className="space-y-4">
            {Object.entries(health.performance).map(([metricKey, metric]) => (
              <div key={metricKey} className="p-3 border border-gray-200 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-sm font-medium text-gray-900 capitalize">
                    {metricKey.replace('_', ' ')}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    metric.trend === 'increasing' ? 'bg-green-100 text-green-800' :
                    metric.trend === 'decreasing' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {metric.trend}
                  </span>
                </div>
                
                <div className="flex justify-between items-end">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{metric.current}</div>
                    <div className="text-xs text-gray-500">Current</div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Threshold: {metric.threshold}</div>
                    <div className={`text-xs ${
                      isMetricWithinThreshold(metricKey, metric) ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {isMetricWithinThreshold(metricKey, metric) ? 'Within limits' : 'Above threshold'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Summary */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">System Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {Object.values(health.services).filter(s => s.status === 'healthy').length}
            </div>
            <div className="text-sm text-gray-600">Services Running</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {health.database.connection ? '✅' : '❌'}
            </div>
            <div className="text-sm text-gray-600">Database</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {health.storage.used_percentage}%
            </div>
            <div className="text-sm text-gray-600">Storage Used</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {Object.entries(health.performance).filter(([metricKey, metric]) => 
                isMetricWithinThreshold(metricKey, metric)
              ).length}/{Object.keys(health.performance).length}
            </div>
            <div className="text-sm text-gray-600">Metrics OK</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SystemHealth;